import { ScalarConsumer, ScalarProvider } from "./Scalar";

export interface DictionaryConsumer<KeyType, ValueType> {
	add(key: KeyType, value: ValueType): void;
	remove(key: KeyType): void;
}

export abstract class DictionaryProvider<KeyType, ValueType> {
	abstract subscribe(subscriber: DictionaryConsumer<KeyType, ValueType>): typeof subscriber;
	abstract unsubscribe(subscriber: DictionaryConsumer<KeyType, ValueType>): void;
	abstract entries: ReadonlyMap<KeyType, ValueType>;
	abstract size: ScalarProvider<number>;
}

export abstract class DictionaryExpression<KeyInType, ValueInType, ValueOutType, KeyOutType = KeyInType>
	extends DictionaryProvider<KeyOutType, ValueOutType>
	implements DictionaryConsumer<KeyInType, ValueInType>
{
	protected _subscriber: DictionaryConsumer<KeyOutType, ValueOutType> | undefined;

	constructor(readonly source: DictionaryProvider<KeyInType, ValueInType>) {
		super();
	}

	abstract add(key: KeyInType, value: ValueInType): void;

	abstract remove(key: KeyInType): void;

	subscribe(subscriber: DictionaryConsumer<KeyOutType, ValueOutType>) {
		if (this._subscriber !== undefined) throw new Error("unexpected");
		this._subscriber = subscriber;
		this.source.subscribe(this);
		return subscriber;
	}

	unsubscribe(subscriber: DictionaryConsumer<KeyOutType, ValueOutType>) {
		if ((<any>global)?.alan_strict && this._subscriber !== subscriber) throw new Error("unexpected");
		this._subscriber = undefined;
		this.source.unsubscribe(this);
	}
}

export abstract class DynamicDictionaryExpression<KeyInType, ValueInType, ResultType>
	extends ScalarProvider<ResultType>
	implements ScalarConsumer<DictionaryProvider<KeyInType, ValueInType>>
{
	private _subscriber: ScalarConsumer<ResultType> | undefined;
	private _value: (ResultType & DictionaryConsumer<KeyInType, ValueInType>) | undefined;
	private _source_dictionary: DictionaryProvider<KeyInType, ValueInType> | undefined;

	constructor(readonly source: ScalarProvider<DictionaryProvider<KeyInType, ValueInType>>) {
		super();
	}

	protected abstract initialize(): ResultType & DictionaryConsumer<KeyInType, ValueInType>;

	get value(): ResultType {
		if (this._subscriber === undefined) {
			return this.initialize();
		} else {
			return this._value!;
		}
	}

	update(value: DictionaryProvider<KeyInType, ValueInType>) {
		if (this._source_dictionary && this._value) {
			this._source_dictionary.unsubscribe(this._value);
		}
		this._source_dictionary = value;
		this._value = this.initialize();
		this._subscriber!.update(this._value);
	}

	subscribe(subscriber: ScalarConsumer<ResultType>): typeof subscriber {
		if (this._subscriber !== undefined) throw new Error("unexpected");
		this._subscriber = subscriber;
		this.source.subscribe(this);
		return subscriber;
	}

	unsubscribe(subscriber: ScalarConsumer<ResultType>): void {
		if ((<any>global)?.alan_strict && this._subscriber !== subscriber) throw new Error("unexpected");
		this._subscriber = undefined;
		this.source.unsubscribe(this);
	}
}
